import React from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Slider,
  Paper,
  FormHelperText,
  Chip,
  Autocomplete,
  Divider,
  Switch,
} from '@mui/material';
import { Controller, Control } from 'react-hook-form';
import { ClientFormData, PropertyType, ListingType } from '@/types';

interface PreferenceFormProps {
  control: Control<ClientFormData>;
  errors: any;
}

const PreferenceForm: React.FC<PreferenceFormProps> = ({ control, errors }) => {
  const commonAmenities = [
    'Pool', 'Gym', 'Parking', 'Elevator', 'Security', 'Balcony', 
    'Garden', 'Fireplace', 'Central Air', 'Hardwood Floors'
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Budget & Financing Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Budget & Financing
        </Typography>
        <Grid container spacing={3}>
          {/* Price Range */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="preferences.priceRange.min"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Min Budget"
                  fullWidth
                  error={!!errors.preferences?.priceRange?.min}
                  helperText={errors.preferences?.priceRange?.min?.message}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="preferences.priceRange.max"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Max Budget"
                  fullWidth
                  error={!!errors.preferences?.priceRange?.max}
                  helperText={errors.preferences?.priceRange?.max?.message}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="preferences.priceRange.currency"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.preferences?.priceRange?.currency}>
                  <InputLabel>Currency</InputLabel>
                  <Select {...field} label="Currency">
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                    <MenuItem value="GBP">GBP</MenuItem>
                  </Select>
                  <FormHelperText>{errors.preferences?.priceRange?.currency?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {/* Financing */}
           <Grid item xs={12}>
            <Divider sx={{ my: 1 }}><Typography variant="caption" color="text.secondary">FINANCING</Typography></Divider>
          </Grid>
          <Grid item xs={12} sm={3}>
             <Controller
              name="preferences.financing.preApproved"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Pre-Approved"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
             <Controller
              name="preferences.financing.downPayment"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Down Payment"
                  fullWidth
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              name="preferences.financing.loanType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Loan Type</InputLabel>
                  <Select {...field} label="Loan Type">
                    <MenuItem value="conventional">Conventional</MenuItem>
                    <MenuItem value="fha">FHA</MenuItem>
                    <MenuItem value="va">VA</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
             <Controller
              name="preferences.financing.creditScore"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label="Credit Score"
                  fullWidth
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

       {/* Timeline Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Timeline
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
             <Controller
              name="preferences.timeline.moveInDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label="Target Move-in Date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={field.value ? (typeof field.value === 'string' ? field.value : new Date(field.value).toISOString().split('T')[0]) : ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="preferences.timeline.urgency"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Urgency</InputLabel>
                  <Select {...field} label="Urgency">
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="preferences.timeline.flexibility"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Flexibility</InputLabel>
                  <Select {...field} label="Flexibility">
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Property Types Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Property & Listing Types
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Property Types</Typography>
            <Controller
              name="preferences.propertyTypes"
              control={control}
              render={({ field }) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                   {Object.values(PropertyType).map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={field.value?.includes(type)}
                          onChange={(e) => {
                            const newValue = e.target.checked
                              ? [...(field.value || []), type]
                              : (field.value || []).filter((t: string) => t !== type);
                            field.onChange(newValue);
                          }}
                        />
                      }
                      label={type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
                    />
                  ))}
                </Box>
              )}
            />
            {errors.preferences?.propertyTypes && (
              <FormHelperText error>{errors.preferences.propertyTypes.message}</FormHelperText>
            )}
          </Grid>
           <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>Listing Types</Typography>
            <Controller
              name="preferences.listingTypes"
              control={control}
              render={({ field }) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                   {Object.values(ListingType).map((type) => (
                    <FormControlLabel
                      key={type}
                      control={
                        <Checkbox
                          checked={field.value?.includes(type)}
                          onChange={(e) => {
                            const newValue = e.target.checked
                              ? [...(field.value || []), type]
                              : (field.value || []).filter((t: string) => t !== type);
                            field.onChange(newValue);
                          }}
                        />
                      }
                      label={type.charAt(0).toUpperCase() + type.slice(1)}
                    />
                  ))}
                </Box>
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Location Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Location Preferences
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
             <Controller
                name="preferences.location.cities"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={value || []}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Preferred Cities"
                        placeholder="Type and press enter to add cities"
                        error={!!errors.preferences?.location?.cities}
                        helperText={errors.preferences?.location?.cities?.message}
                      />
                    )}
                  />
                )}
              />
          </Grid>
          <Grid item xs={12}>
             <Controller
                name="preferences.location.neighborhoods"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={value || []}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Preferred Neighborhoods"
                        placeholder="Type and press enter to add neighborhoods"
                        error={!!errors.preferences?.location?.neighborhoods}
                        helperText={errors.preferences?.location?.neighborhoods?.message}
                      />
                    )}
                  />
                )}
              />
          </Grid>
        </Grid>
      </Paper>

      {/* Features Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Property Features
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography gutterBottom>Bedrooms</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                 <Controller
                  name="preferences.features.bedrooms.min"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" label="Min" size="small" onChange={(e) => field.onChange(Number(e.target.value))} />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                 <Controller
                  name="preferences.features.bedrooms.max"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" label="Max" size="small" onChange={(e) => field.onChange(Number(e.target.value))} />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
           <Grid item xs={12} md={4}>
            <Typography gutterBottom>Bathrooms</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                 <Controller
                  name="preferences.features.bathrooms.min"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" label="Min" size="small" onChange={(e) => field.onChange(Number(e.target.value))} />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                 <Controller
                  name="preferences.features.bathrooms.max"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" label="Max" size="small" onChange={(e) => field.onChange(Number(e.target.value))} />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
           <Grid item xs={12} md={4}>
            <Typography gutterBottom>Square Footage</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                 <Controller
                  name="preferences.features.squareFootage.min"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" label="Min" size="small" onChange={(e) => field.onChange(Number(e.target.value))} />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                 <Controller
                  name="preferences.features.squareFootage.max"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} type="number" label="Max" size="small" onChange={(e) => field.onChange(Number(e.target.value))} />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom sx={{ mt: 2 }}>Preferred Amenities</Typography>
            <Controller
              name="preferences.features.amenities"
              control={control}
              render={({ field: { value, onChange } }) => (
                 <Autocomplete
                    multiple
                    freeSolo
                    options={commonAmenities}
                    value={value || []}
                    onChange={(_, newValue) => onChange(newValue)}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Amenities"
                        placeholder="Select or type amenities"
                      />
                    )}
                  />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Matching Profile & Behavior */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Matching Profile & Behavior
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Priorities (0-10)</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Adjust the sliders to indicate what is most important to the client.
            </Typography>
            <Grid container spacing={4}>
              {[
                { name: 'matchingProfile.priority.price', label: 'Price' },
                { name: 'matchingProfile.priority.location', label: 'Location' },
                { name: 'matchingProfile.priority.features', label: 'Features' },
                { name: 'matchingProfile.priority.timeline', label: 'Timeline' },
                { name: 'matchingProfile.priority.quality', label: 'Quality/Condition' },
              ].map((item) => (
                <Grid item xs={12} sm={6} key={item.name}>
                    <Typography gutterBottom>{item.label}</Typography>
                    <Controller
                      name={item.name as any}
                      control={control}
                      defaultValue={5}
                      render={({ field }) => (
                        <Slider
                          {...field}
                          value={typeof field.value === 'number' ? field.value : 5}
                          onChange={(_, value) => field.onChange(value)}
                          min={0}
                          max={10}
                          step={1}
                          marks
                          valueLabelDisplay="auto"
                        />
                      )}
                    />
                </Grid>
              ))}
            </Grid>
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="matchingProfile.behavior.searchFrequency"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Search Frequency</InputLabel>
                  <Select {...field} label="Search Frequency">
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="matchingProfile.behavior.preferredContactMethod"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Preferred Contact</InputLabel>
                  <Select {...field} label="Preferred Contact">
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="app">App</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PreferenceForm;
